import type { OcrQaResult, OcrQaTier } from './types';

/**
 * URL-hash keys for the OCR-QA feature's deep-linkable overlays, bound via
 * `useHashParamDialog` (see `@/hooks/use-hash-dialog`). The verdict overlay
 * carries the verdict kind (`accepted` / `rejected`) as a hash param so the
 * confirmation dialog is deep-linkable and closes on browser Back.
 */
export const OCR_QA_HASH = {
  /** `#verdict-container/<accepted|rejected>` — container-level verdict confirmation. */
  verdictContainer: 'verdict-container',
  /** `#verdict-page/<resultId>/<accepted|rejected>` — single-page verdict confirmation. */
  verdictPage: 'verdict-page'
} as const;

export type OcrQaHashKey = (typeof OCR_QA_HASH)[keyof typeof OCR_QA_HASH];

/**
 * Per-tier `qa_stage` values that mark a result as still actionable (awaiting
 * a verdict) at that tier. Shared by the container-level progress stats and
 * the per-page accept/reject buttons.
 */
export const OCR_QA_ACTIVE_STAGES: Record<OcrQaTier, string[]> = {
  1: ['pending_qa1', 'in_qa1'],
  2: ['pending_qa2', 'in_qa2'],
  3: ['pending_qa3', 'in_qa3']
};

export interface ResultPage {
  /** 1-based ordinal within this result's `pages` array — the key used for `page_notes`. */
  pageNumber: number;
  /** Raw page label from the backend (e.g. "PG-002"), for display; null when unknown. */
  rawLabel: string | null;
}

/**
 * Page-number → label lookup over the CONTAINER's page list, as serialized
 * onto every result by `OcrResultResource`.
 *
 * Deliberately generous, and deliberately NOT a statement about which pages a
 * result covers — that is `resolveOwnedPages` in `@/features/ocr`. Using this
 * for scope is the bug it caused: the verdict dialogs enumerated it and asked a
 * reviewer for a rejection reason on all twelve pages of a container for a
 * single-page result. Its remaining job is resolving a label for a page number
 * that already appears in `page_notes`, where covering every page the container
 * has is the point.
 */
export function resolveResultPages(result: OcrQaResult): ResultPage[] {
  if (result.pages && result.pages.length > 0) {
    return result.pages.map((p, idx) => ({
      pageNumber: idx + 1,
      rawLabel: p.page_number || null
    }));
  }
  return [{ pageNumber: 1, rawLabel: result.page_number ?? null }];
}
